/**
 * CheckoutDrawer — criar / editar checkout (URL personalizada).
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { checkoutsV2API } from '../../../services/api/v2/checkouts';
import Dropdown from '../../ui/Dropdown';
import Drawer from './Drawer';

const EMPTY = {
  name: '',
  plan_id: '',
  slug: '',
  require_phone: true,
  require_address: true,
};

const CheckoutDrawer = ({ isOpen, onClose, productId, plans = [], checkout, onSaved }) => {
  const isEdit = !!checkout?.id;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setTopError('');
    if (checkout) {
      setForm({
        name: checkout.name || '',
        plan_id: checkout.plan_id || '',
        slug: checkout.slug || '',
        require_phone: checkout.require_phone !== false,
        require_address: checkout.require_address !== false,
      });
    } else {
      const firstPlan = plans[0];
      setForm({ ...EMPTY, plan_id: firstPlan?.id || '' });
    }
  }, [isOpen, checkout, plans]);

  const update = (f, v) => {
    setForm((p) => ({ ...p, [f]: v }));
    setErrors((p) => ({ ...p, [f]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = 'Nome é obrigatório';
    if (!form.plan_id) err.plan_id = 'Selecione um plano';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    try {
      setSubmitting(true);
      setTopError('');
      const payload = {
        name: form.name.trim(),
        plan_id: Number(form.plan_id),
        slug: form.slug.trim() || null,
        require_phone: !!form.require_phone,
        require_address: !!form.require_address,
      };
      let res;
      if (isEdit) {
        res = await checkoutsV2API.update(checkout.id, payload);
      } else {
        res = await checkoutsV2API.create({ ...payload, product_id: productId });
      }
      const saved = res.data?.checkout || res.data?.data || res.data;
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const data = err.response?.data || {};
      setTopError(data.message || err.message || 'Erro ao salvar checkout');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar checkout' : 'Novo checkout'}
      subtitle={isEdit ? form.name : 'URL personalizada vinculada a um plano'}
      icon="mdi:cart-outline"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={isEdit ? 'Salvar checkout' : 'Criar checkout'}
    >
      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Nome do checkout</span>
        <input
          className="drawer-input"
          type="text"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Ex: Checkout padrão, Black Friday..."
        />
        {errors.name && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.name}
          </span>
        )}
      </div>

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Plano vinculado</span>
        <Dropdown
          compact
          placeholder="Selecione um plano"
          value={form.plan_id || ''}
          onChange={(v) => update('plan_id', Number(v))}
          options={plans.map((pl) => ({
            value: pl.id,
            label: pl.name,
            icon: 'mdi:credit-card-multiple-outline',
          }))}
        />
        {errors.plan_id && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.plan_id}
          </span>
        )}
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Slug (opcional)</span>
        <input
          className="drawer-input"
          type="text"
          value={form.slug}
          onChange={(e) => update('slug', e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())}
          placeholder="black-friday"
        />
        <span className="drawer-help">URL final: app.pagmus.com.br/checkout/{form.slug || 'slug-aqui'}</span>
      </div>

      {/* Switches */}
      <div className="drawer-switch">
        <div className="drawer-switch__label">
          <span className="drawer-switch__label-title">Solicitar telefone</span>
          <span className="drawer-switch__label-desc">Campo telefone obrigatório no checkout</span>
        </div>
        <button
          type="button"
          className={`drawer-switch__input ${form.require_phone ? 'is-on' : ''}`}
          onClick={() => update('require_phone', !form.require_phone)}
          aria-pressed={form.require_phone}
        />
      </div>

      <div className="drawer-switch">
        <div className="drawer-switch__label">
          <span className="drawer-switch__label-title">Solicitar endereço</span>
          <span className="drawer-switch__label-desc">Campo endereço obrigatório (necessário pra produtos físicos)</span>
        </div>
        <button
          type="button"
          className={`drawer-switch__input ${form.require_address ? 'is-on' : ''}`}
          onClick={() => update('require_address', !form.require_address)}
          aria-pressed={form.require_address}
        />
      </div>
    </Drawer>
  );
};

export default CheckoutDrawer;
